import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  BleClient,
  ScanMode,
  ScanResult,
  RequestBleDeviceOptions,
  dataViewToText,
  textToDataView,
} from '@capacitor-community/bluetooth-le';

export class Device {
  name: string;
  id: string;
  rssi: number;
  advertising: DataView;
}

export class DeviceConnectionStatus {
  deviceId: string;
  connected: boolean;
  connectionData: any;
}

export class DeviceCharacteristicData {
  deviceId: string;
  serviceUUID: string;
  characteristicUUID: string;
  commandType: string;
  commandData?: string;
  data?: string;
  error?: any;
}

export class MultipleCharacteristicRead {
  deviceId: string;
  serviceUUID: string;
  characteristicUUID: string;
  subject: Subject<DeviceCharacteristicData>;
}

export enum BLEErrorEnum {
  OTHER = '0',
  BLE_NOT_ENABLED = '1',
  NO_CONNECTION = '2',
  SCAN_ALREADY_RUNNING = '3',
  READ_ALREADY_RUNNING = '4',
  NO_MATCHING_READ = '5',
}

export class BLEError {
  type: BLEErrorEnum;
  info?: any;
}

@Injectable({
  providedIn: 'root',
})
export class BLEService {
  private static BLE_ENABLED_CHECK_TIME: number = 10000; // 10s
  private static DEFAULT_SCAN_TIME: number = 5000; // 5s

  // diff between Subjects: https://www.learnrxjs.io/learn-rxjs/subjects

  private onIsEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private isEnabled: boolean = false;
  private bleCheckTimeout = null;
  private scanRunning: boolean = false;
  private scanTimeout = null;

  private devices$: BehaviorSubject<Device[]> = null;

  private multipleRead: MultipleCharacteristicRead = null;

  private openConnections: Map<string, DeviceConnectionStatus> = new Map<string, DeviceConnectionStatus>();

  constructor(private log: NGXLogger) {
    BleClient.initialize().then(
      (ok) => {
        // periodically check the status of the BLE
        this.checkBLEState();
      },
      (error) => {
        this.log.error(error);
      }
    );
  }

  ngOnDestroy(): void {
    // stop running scans and connections and so on
    if (this.scanRunning) {
      this.stopScan();
    }
    for (let [deviceId, deviceConnectionStatus] of this.openConnections) {
      this.disconnectFromDevice(deviceId);
    }
    if (this.bleCheckTimeout) {
      clearTimeout(this.bleCheckTimeout);
    }
  }

  private checkBLEState() {
    BleClient.isEnabled().then(
      (enabled) => {
        // only change if was not enabled before
        if (!this.isEnabled) {
          this.isEnabled = true;
          this.onIsEnabled$.next(true);
        }
      },
      (disabled) => {
        // only change if was enabled before
        if (this.isEnabled) {
          this.isEnabled = false;
          this.onIsEnabled$.next(false);
          // TODO request enable here?
        }
      }
    );
    this.bleCheckTimeout = setTimeout(() => {
      this.checkBLEState();
    }, BLEService.BLE_ENABLED_CHECK_TIME);
  }

  private isBLEStateOK(): boolean {
    if (!this.isEnabled) {
      this.log.error('ble not enabled');
      return false;
    }
    return true;
  }

  private scanCallback(result: ScanResult, devices: Device[]) {
    this.log.info('Scan result:', result.localName, result.rssi, result.device.deviceId);
    if (result) {
      // map scan results to devices
      const device_: Device = {
        name: result.localName,
        id: result.device.deviceId,
        rssi: result.rssi,
        advertising: result.rawAdvertisement,
      };
      devices.push(device_);
      this.devices$.next(devices);
    }
  }

  scan(serviceUUIDs?: string[], scanTimeMS?: number): Observable<Device[]> {
    let err: BLEError = null;
    if (!this.isBLEStateOK()) {
      err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
    }
    if (!err && this.scanRunning) {
      this.log.error('scan already running -> stop the other scan first or wait until  finished');
      err = { type: BLEErrorEnum.SCAN_ALREADY_RUNNING };
    }
    if (err) {
      const s$ = new BehaviorSubject<Device[]>(null);
      s$.error(err);
      s$.complete();
      return s$;
    }

    if (!scanTimeMS) {
      scanTimeMS = BLEService.DEFAULT_SCAN_TIME;
    }

    let scanOptions: RequestBleDeviceOptions = {
      scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
    };
    if (serviceUUIDs) {
      scanOptions.services = serviceUUIDs;
    }
    this.scanRunning = true;
    const devices: Device[] = [];
    this.devices$ = new BehaviorSubject(devices);
    BleClient.requestLEScan(scanOptions, (result) => this.scanCallback(result, devices)).then(
      (ok) => {
        // do nothing ;)
      },
      (error) => {
        this.log.error('error while scanning', error);
        if (this.scanTimeout) {
          clearTimeout(this.scanTimeout);
        }
        this.scanTimeout = null;
        this.scanRunning = false;
        this.devices$.error(error);
        this.devices$.complete();
      }
    );

    this.scanTimeout = setTimeout(() => {
      this.log.error('STOPPING SCAN AFTER TIMEOUT');
      this.stopScan();
    }, scanTimeMS);

    return this.devices$;
  }

  cancelScan() {
    if (this.scanRunning && this.scanTimeout) {
      this.log.error('STOPPING SCAN AFTER CANCELLATION');
      this.stopScan();
    }
  }

  private stopScan() {
    BleClient.stopLEScan().then(
      (ok) => {
        if (this.scanTimeout) {
          clearTimeout(this.scanTimeout);
        }
        this.scanTimeout = null;
        this.scanRunning = false;
        this.devices$.complete();
      },
      (error) => {
        this.log.error('error while stopping scan', error);
      }
    );
  }

  private onConnectDevice(deviceId: string, deviceConnectionStatus$) {
    this.log.error('device connected', deviceId);
    const connectionStatus: DeviceConnectionStatus = {
      deviceId: deviceId,
      connected: true,
      connectionData: null,
    };
    this.openConnections.set(deviceId, connectionStatus);
    deviceConnectionStatus$.next(connectionStatus);
  }

  private onDisconnectDevice(deviceId: string, deviceConnectionStatus$) {
    this.log.error('device disconnected', deviceId);
    const connectionStatus: DeviceConnectionStatus = {
      deviceId: deviceId,
      connected: false,
      connectionData: null,
    };
    this.resetConnection();
    this.openConnections.delete(deviceId);
    deviceConnectionStatus$.next(connectionStatus);
    deviceConnectionStatus$.complete();
  }

  connectToDevice(deviceId: string): Observable<DeviceConnectionStatus> {
    let err: BLEError = null;
    if (!this.isBLEStateOK()) {
      err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
    }
    if (err) {
      const s$ = new BehaviorSubject<DeviceConnectionStatus>(null);
      s$.error(err);
      s$.complete();
      return s$;
    }

    const deviceConnectionStatus$ = new Subject<DeviceConnectionStatus>();
    BleClient.connect(deviceId, (deviceId) => this.onDisconnectDevice(deviceId, deviceConnectionStatus$)).then(
      (ok) => {
        this.onConnectDevice(deviceId, deviceConnectionStatus$);
      },
      (error) => {
        this.onDisconnectDevice(deviceId, deviceConnectionStatus$);
      }
    );
    return deviceConnectionStatus$;
  }

  private resetConnection() {
    if (this.multipleRead) {
      BleClient.stopNotifications(this.multipleRead.deviceId, this.multipleRead.serviceUUID, this.multipleRead.characteristicUUID).then(
        (ok) => {
          this.stopMultipleRead();
        }
      );
    }
  }

  disconnectFromDevice(deviceId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let err: BLEError = null;
      if (!this.isBLEStateOK()) {
        err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
      }
      if (err) {
        reject(err);
      }

      BleClient.disconnect(deviceId).then(
        (success) => {
          this.log.debug('disconnected from device', deviceId, success);
          resolve();
        },
        (error) => {
          this.log.error('error while disconnecting from device', deviceId, error);
          reject(error);
        }
      );
    });
  }

  getConnectedDevices(): DeviceConnectionStatus[] {
    const devices: DeviceConnectionStatus[] = [];
    for (let [deviceId, deviceConnectionStatus] of this.openConnections) {
      devices.push(deviceConnectionStatus);
    }
    return devices;
  }

  readCharacteristicOnce(deviceId: string, serviceUUID: string, characteristicUUID: string): Promise<DeviceCharacteristicData> {
    return new Promise((resolve, reject) => {
      let err: BLEError = null;
      if (!this.isBLEStateOK()) {
        err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
      }
      if (!err && !this.openConnections.has(deviceId)) {
        this.log.error('no connection to device -> you have to connect first');
        err = { type: BLEErrorEnum.NO_CONNECTION };
      }
      if (!err && this.multipleRead) {
        this.log.error('another long running read is active');
        err = { type: BLEErrorEnum.READ_ALREADY_RUNNING };
      }
      if (err) {
        reject(err);
      }

      BleClient.read(deviceId, serviceUUID, characteristicUUID).then(
        (data) => {
          const charData: DeviceCharacteristicData = {
            deviceId: deviceId,
            serviceUUID: serviceUUID,
            characteristicUUID: characteristicUUID,
            commandType: 'READ',
            data: dataViewToText(data),
          };
          resolve(charData);
        },
        (error) => {
          this.log.error('error reading characteristic', deviceId, serviceUUID, characteristicUUID, error);
          const charData: DeviceCharacteristicData = {
            deviceId: deviceId,
            serviceUUID: serviceUUID,
            characteristicUUID: characteristicUUID,
            commandType: 'READ',
            error: error,
          };
          err = { type: BLEErrorEnum.OTHER, info: charData };
          reject(err);
        }
      );
    });
  }

  private onMultipleDataReceived(
    data: DataView,
    deviceId: string,
    serviceUUID: string,
    characteristicUUID: string,
    subject: Subject<DeviceCharacteristicData>
  ) {
    const charData: DeviceCharacteristicData = {
      deviceId: deviceId,
      serviceUUID: serviceUUID,
      characteristicUUID: characteristicUUID,
      commandType: 'READ',
      data: dataViewToText(data),
    };
    subject.next(charData);
  }

  readCharacteristicMultiple(deviceId: string, serviceUUID: string, characteristicUUID: string): Observable<DeviceCharacteristicData> {
    let err: BLEError = null;
    if (!this.isBLEStateOK()) {
      err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
    }
    if (!err && !this.openConnections.has(deviceId)) {
      this.log.error('no connection to device -> you have to connect first');
      err = { type: BLEErrorEnum.NO_CONNECTION };
    }
    if (!err && this.multipleRead) {
      this.log.error('another long running read is active');
      err = { type: BLEErrorEnum.READ_ALREADY_RUNNING };
    }

    const subject = new Subject<DeviceCharacteristicData>();
    if (err) {
      subject.error(err);
      subject.complete();
      return subject;
    }

    BleClient.startNotifications(deviceId, serviceUUID, characteristicUUID, (data) =>
      this.onMultipleDataReceived(data, deviceId, serviceUUID, characteristicUUID, subject)
    ).then(
      (ok) => {
        // do nothing ;)
      },
      (error) => {
        this.log.error('error reading characteristic', deviceId, serviceUUID, characteristicUUID, error);
        const charData: DeviceCharacteristicData = {
          deviceId: deviceId,
          serviceUUID: serviceUUID,
          characteristicUUID: characteristicUUID,
          commandType: 'READ',
          error: error,
        };
        err = { type: BLEErrorEnum.OTHER, info: charData };
        subject.error(err);
        subject.complete();
      }
    );

    this.multipleRead = {
      deviceId: deviceId,
      serviceUUID: serviceUUID,
      characteristicUUID: characteristicUUID,
      subject: subject,
    };

    return subject;
  }

  cancelReadCharacteristicMultiple(deviceId: string, serviceUUID: string, characteristicUUID: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let err: BLEError = null;
      if (!this.isBLEStateOK()) {
        err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
      }
      if (!err && !this.openConnections.has(deviceId)) {
        this.log.error('no connection to device -> you have to connect first');
        err = { type: BLEErrorEnum.NO_CONNECTION };
      }
      if (
        !err &&
        (this.multipleRead.deviceId != deviceId ||
          this.multipleRead.serviceUUID != serviceUUID ||
          this.multipleRead.characteristicUUID != characteristicUUID)
      ) {
        this.log.error('no matching long running read active');
        err = { type: BLEErrorEnum.NO_MATCHING_READ };
      }
      if (err) {
        reject(err);
      }

      BleClient.stopNotifications(deviceId, serviceUUID, characteristicUUID).then(
        (ok) => {
          this.stopMultipleRead();
          resolve();
        },
        (error) => {
          err = { type: BLEErrorEnum.OTHER, info: error };
          this.stopMultipleRead();
          reject(err);
        }
      );
    });
  }

  private stopMultipleRead() {
    if (this.multipleRead) {
      this.log.debug('stopping multiple read', this.multipleRead);
      this.multipleRead.subject.complete();
      this.multipleRead = null;
    }
  }

  writeCharacteristic(deviceId: string, serviceUUID: string, characteristicUUID: string, value: string): Promise<DeviceCharacteristicData> {
    return new Promise((resolve, reject) => {
      let err: BLEError = null;
      if (!this.isBLEStateOK()) {
        err = { type: BLEErrorEnum.BLE_NOT_ENABLED };
      }
      if (!err && !this.openConnections.has(deviceId)) {
        this.log.error('no connection to device -> you have to connect first');
        err = { type: BLEErrorEnum.NO_CONNECTION };
      }
      if (err) {
        reject(err);
      }

      BleClient.write(deviceId, serviceUUID, characteristicUUID, textToDataView(value)).then(
        (ok) => {
          const charData: DeviceCharacteristicData = {
            deviceId: deviceId,
            serviceUUID: serviceUUID,
            characteristicUUID: characteristicUUID,
            commandType: 'WRITE',
            commandData: value,
            data: value,
          };
          resolve(charData);
        },
        (error) => {
          this.log.error('error writing characteristic', deviceId, serviceUUID, characteristicUUID, error);
          const charData: DeviceCharacteristicData = {
            deviceId: deviceId,
            serviceUUID: serviceUUID,
            characteristicUUID: characteristicUUID,
            commandType: 'WRITE',
            commandData: value,
            error: error,
          };
          err = { type: BLEErrorEnum.OTHER, info: charData };
          reject(err);
        }
      );
    });
  }

  isBLEEnabled(): boolean {
    return this.isEnabled;
  }

  onIsEnabledChanged(): Observable<boolean> {
    return this.onIsEnabled$;
  }
}
